import { useParams, Link } from "react-router-dom";
import {
  useAdminChildDetail,
  type ChildDetail,
} from "../../hooks/useAdminUsers";

/**
 * ChildDetailPage - Admin page to view individual child details.
 *
 * Displays:
 * - Child profile info with parent link
 * - Overall stats (stars, badges, playtime, challenges)
 * - Progress across all disciplines
 * - Recent activity timeline
 * - Activity heatmap for last 30 days
 */
export function ChildDetailPage() {
  const { childId } = useParams<{ childId: string }>();
  const { child, isLoading, error } = useAdminChildDetail(childId);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !child) {
    return (
      <div className="space-y-6">
        <BackLink />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <ErrorIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-red-800">
                Error Loading Child
              </h2>
              <p className="text-sm text-red-600 mt-1">
                {error || "Child not found"}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <BackLink parentId={child.parent.id} parentName={child.parent.name} />

      {/* Child header */}
      <ChildHeader child={child} />

      {/* Overall Progress Stats */}
      <OverallStats stats={child.stats} />

      {/* Discipline Progress */}
      <DisciplineProgress
        disciplines={child.disciplines}
        childId={child.child.id}
      />

      {/* Bottom row: Activity Timeline + Activity Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ActivityTimeline activities={child.recentActivity} />
        <ActivityHeatmap history={child.activityHistory} />
      </div>
    </div>
  );
}

// ----- Main Section Components -----

function ChildHeader({ child }: { child: ChildDetail }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sunshine-200 to-sunshine-400 flex items-center justify-center flex-shrink-0 shadow-md">
            <span className="text-sunshine-800 font-bold text-2xl">
              {child.child.name.charAt(0).toUpperCase()}
            </span>
          </div>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {child.child.name}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
              <span>
                Parent:{" "}
                <Link
                  to={`/admin/users/${child.parent.id}`}
                  className="text-magic-600 hover:text-magic-700 font-medium"
                >
                  {child.parent.name || child.parent.email}
                </Link>
              </span>
            </div>
            <p className="text-xs text-gray-400 font-mono mt-1">
              ID: {child.child.id}
            </p>
          </div>
        </div>

        {/* Timestamps */}
        <div className="text-right text-sm">
          <p className="text-gray-500">
            Created: {formatDate(child.child.createdAt)}
          </p>
          <p className="text-gray-500">
            Last active:{" "}
            {child.child.lastActiveAt
              ? formatRelativeTime(child.child.lastActiveAt)
              : "Never"}
          </p>
        </div>
      </div>
    </div>
  );
}

function OverallStats({ stats }: { stats: ChildDetail["stats"] }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Overall Progress
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<StarIcon className="w-6 h-6" />}
          iconBg="bg-sunshine-100"
          iconColor="text-sunshine-600"
          value={stats.totalStars}
          label="Stars"
        />
        <StatCard
          icon={<BadgeIcon className="w-6 h-6" />}
          iconBg="bg-magic-100"
          iconColor="text-magic-600"
          value={stats.totalBadges}
          label="Badges"
        />
        <StatCard
          icon={<ClockIcon className="w-6 h-6" />}
          iconBg="bg-sky-100"
          iconColor="text-sky-600"
          value={formatPlaytime(stats.totalPlaytimeMinutes)}
          label="Playtime"
        />
        <StatCard
          icon={<CheckCircleIcon className="w-6 h-6" />}
          iconBg="bg-meadow-100"
          iconColor="text-meadow-600"
          value={stats.challengesCompleted}
          label="Challenges"
        />
      </div>
    </div>
  );
}

function DisciplineProgress({
  disciplines,
  childId,
}: {
  disciplines: ChildDetail["disciplines"];
  childId: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Discipline Progress
        </h2>
        <span className="text-xs text-gray-400">
          Click a discipline for detailed view
        </span>
      </div>
      <div className="space-y-4">
        {disciplines.map((discipline) => (
          <DisciplineCard
            key={discipline.slug}
            discipline={discipline}
            childId={childId}
          />
        ))}
        {disciplines.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>No disciplines available</p>
          </div>
        )}
      </div>
    </div>
  );
}

function DisciplineCard({
  discipline,
  childId,
}: {
  discipline: ChildDetail["disciplines"][0];
  childId: string;
}) {
  const config = DISCIPLINE_CONFIG[discipline.slug] ?? DISCIPLINE_CONFIG.math!;

  return (
    <Link
      to={`/admin/children/${childId}/progress?discipline=${discipline.slug}`}
      className="flex items-center gap-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
    >
      {/* Icon */}
      <div
        className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}
      >
        <span className="text-2xl">{config.emoji}</span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{discipline.name}</h3>
            <span className="text-xs text-gray-500">({discipline.character})</span>
          </div>
          <span className="text-sm font-medium text-gray-700">
            Level {discipline.currentLevel} of {discipline.maxLevel}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="relative">
          <ProgressBar
            current={discipline.currentLevel}
            max={discipline.maxLevel}
            color={config.progressColor}
          />
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <StarIcon className="w-3.5 h-3.5 text-sunshine-500" />
            {discipline.stars} stars
          </span>
          <span>
            Last played:{" "}
            {discipline.lastPlayedAt
              ? formatRelativeTime(discipline.lastPlayedAt)
              : "Never"}
          </span>
          <span className="ml-auto text-magic-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            View details
            <ChevronRightIcon className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function ActivityTimeline({
  activities,
}: {
  activities: ChildDetail["recentActivity"];
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Recent Activity
      </h2>

      {activities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <ActivityIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No activity yet</p>
        </div>
      ) : (
        <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2">
          {activities.map((activity, index) => (
            <ActivityItem key={index} activity={activity} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityItem({
  activity,
}: {
  activity: ChildDetail["recentActivity"][0];
}) {
  const isChallenge = activity.type === "challenge_completed";
  const isBadge = activity.type === "badge_earned";

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      {/* Icon */}
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
          isBadge
            ? "bg-magic-100"
            : isChallenge
              ? "bg-meadow-100"
              : "bg-gray-100"
        }`}
      >
        {isBadge ? (
          <BadgeIcon className="w-4 h-4 text-magic-600" />
        ) : isChallenge ? (
          <CheckCircleIcon className="w-4 h-4 text-meadow-600" />
        ) : (
          <StarIcon className="w-4 h-4 text-gray-500" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">{activity.description}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-400">
            {formatRelativeTime(activity.timestamp)}
          </span>
          {activity.stars && (
            <span className="inline-flex items-center gap-0.5">
              {[...Array(activity.stars)].map((_, i) => (
                <StarIcon
                  key={i}
                  className="w-3 h-3 text-sunshine-500"
                  filled
                />
              ))}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityHeatmap({
  history,
}: {
  history: ChildDetail["activityHistory"];
}) {
  // Get max values for scaling
  const maxChallenges = Math.max(...history.map((d) => d.challengesCompleted), 1);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Activity (Last 30 Days)
      </h2>

      <div className="space-y-6">
        {/* Heatmap grid */}
        <div>
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="text-center text-[10px] text-gray-400 font-medium"
              >
                {day}
              </div>
            ))}
          </div>

          <HeatmapGrid history={history} maxChallenges={maxChallenges} />

          {/* Legend */}
          <div className="flex items-center justify-end gap-2 mt-3">
            <span className="text-xs text-gray-400">Less</span>
            <div className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((level) => (
                <div
                  key={level}
                  className={`w-3 h-3 rounded-sm ${getHeatmapColor(level / 4)}`}
                />
              ))}
            </div>
            <span className="text-xs text-gray-400">More</span>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-500 mb-1">Total Challenges</p>
            <p className="text-xl font-bold text-gray-900">
              {history.reduce((sum, d) => sum + d.challengesCompleted, 0)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Active Days</p>
            <p className="text-xl font-bold text-gray-900">
              {history.filter((d) => d.challengesCompleted > 0).length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function HeatmapGrid({
  history,
  maxChallenges,
}: {
  history: ChildDetail["activityHistory"];
  maxChallenges: number;
}) {
  // Create a map for quick lookup
  const activityMap = new Map(history.map((d) => [d.date, d]));

  // Generate grid data for last 5 weeks (35 days to fill 5 complete rows)
  const today = new Date();
  const cells: Array<{
    date: string;
    challenges: number;
    minutes: number;
  }> = [];

  // Start from 34 days ago to fill 5 complete weeks
  for (let i = 34; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateKey = date.toISOString().split("T")[0] ?? "";
    const data = activityMap.get(dateKey);

    cells.push({
      date: dateKey,
      challenges: data?.challengesCompleted ?? 0,
      minutes: data?.minutesPlayed ?? 0,
    });
  }

  // Arrange into weeks (7 days per row)
  // First, find the day of week for the first cell and add padding
  const firstCell = cells[0];
  const firstDate = firstCell ? new Date(firstCell.date) : new Date();
  const firstDayOfWeek = firstDate.getDay(); // 0 = Sunday

  // Add empty cells for padding
  const paddedCells = [
    ...Array(firstDayOfWeek).fill(null),
    ...cells,
  ];

  // Group into rows of 7
  const rows: Array<typeof paddedCells> = [];
  for (let i = 0; i < paddedCells.length; i += 7) {
    rows.push(paddedCells.slice(i, i + 7));
  }

  return (
    <div className="space-y-1">
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="grid grid-cols-7 gap-1">
          {row.map((cell, cellIndex) =>
            cell === null ? (
              <div key={cellIndex} className="w-full aspect-square" />
            ) : (
              <div
                key={cell.date}
                className={`w-full aspect-square rounded-sm ${getHeatmapColor(
                  cell.challenges / maxChallenges
                )} cursor-pointer transition-transform hover:scale-110`}
                title={`${formatDate(cell.date)}: ${cell.challenges} challenges, ${cell.minutes} min`}
              />
            )
          )}
        </div>
      ))}
    </div>
  );
}

// ----- Shared Components -----

function ProgressBar({
  current,
  max,
  color,
}: {
  current: number;
  max: number;
  color: string;
}) {
  const percent = Math.round((current / max) * 100);

  return (
    <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ease-out ${color}`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}

function StatCard({
  icon,
  iconBg,
  iconColor,
  value,
  label,
}: {
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  value: string | number;
  label: string;
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-gray-50">
      <div
        className={`w-12 h-12 rounded-xl ${iconBg} ${iconColor} flex items-center justify-center flex-shrink-0`}
      >
        {icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
      </div>
    </div>
  );
}

function BackLink({
  parentId,
  parentName,
}: {
  parentId?: string;
  parentName?: string | null;
}) {
  if (parentId) {
    return (
      <Link
        to={`/admin/users/${parentId}`}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to {parentName ? `${parentName}'s Children` : "Parent"}
      </Link>
    );
  }

  return (
    <Link
      to="/admin/users"
      className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
    >
      <ArrowLeftIcon className="w-4 h-4" />
      Back to Users
    </Link>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-40" />

      {/* Header skeleton */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-200" />
          <div className="flex-1">
            <div className="h-8 bg-gray-200 rounded w-48 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-64 mb-1" />
            <div className="h-3 bg-gray-100 rounded w-80" />
          </div>
        </div>
      </div>

      {/* Stats skeleton */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>

      {/* Disciplines skeleton */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>

      {/* Bottom row skeleton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-40 mb-4" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-48 mb-4" />
          <div className="h-32 bg-gray-100 rounded" />
        </div>
      </div>
    </div>
  );
}

// ----- Configuration -----

const DISCIPLINE_CONFIG: Record<
  string,
  { emoji: string; bgColor: string; progressColor: string }
> = {
  math: {
    emoji: "🔢",
    bgColor: "bg-luna-light",
    progressColor: "bg-luna",
  },
  spelling: {
    emoji: "🔤",
    bgColor: "bg-petal-light",
    progressColor: "bg-petal",
  },
  patterns: {
    emoji: "🎨",
    bgColor: "bg-gradient-to-br from-prism-red to-prism-purple",
    progressColor: "bg-gradient-to-r from-prism-red via-prism-yellow to-prism-blue",
  },
  typing: {
    emoji: "⌨️",
    bgColor: "bg-whiskers-light",
    progressColor: "bg-whiskers",
  },
  programming: {
    emoji: "🤖",
    bgColor: "bg-pixel-light",
    progressColor: "bg-pixel",
  },
};

// ----- Utility Functions -----

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatPlaytime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function getHeatmapColor(intensity: number): string {
  if (intensity === 0) return "bg-gray-100";
  if (intensity <= 0.25) return "bg-meadow-200";
  if (intensity <= 0.5) return "bg-meadow-300";
  if (intensity <= 0.75) return "bg-meadow-400";
  return "bg-meadow-500";
}

// ----- Icons -----

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
      />
    </svg>
  );
}

function StarIcon({
  className,
  filled,
}: {
  className?: string;
  filled?: boolean;
}) {
  return (
    <svg
      className={className}
      fill={filled ? "currentColor" : "none"}
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={filled ? 0 : 2}
        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
      />
    </svg>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
      />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ActivityIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 10V3L4 14h7v7l9-11h-7z"
      />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
      />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  );
}

export default ChildDetailPage;
